import React, { useEffect } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { isEmpty } from 'lodash-es';

import FormElementWrapper from 'components/formElements/FormElementWrapper';
import DropdownBase, {
  IDropdownProps as IBaseDropdownProps,
  OptionValue
} from 'components/core/Dropdown/Dropdown';
import { IFormElementProps, ComponentWithoutValue } from 'models/form';
import {
  getErrorMessage,
  onChangeFormElementComponent,
  parseErrorMessageToFieldBox
} from 'utilities/form';
import { trackDropdownSelect } from 'utilities';
import { GA_ACTIONS } from 'consts/analytics/ga';

interface IDropdownProps
  extends ComponentWithoutValue<IBaseDropdownProps>,
    IFormElementProps {
  defaultValue?: OptionValue;
  hideDropdown?: boolean;
  gaAction?: string;
  styleDropdownAsDiv?: boolean;
}

const Dropdown = ({
  className,
  input,
  meta,
  onChange,
  defaultValue,
  options,
  hideDropdown,
  oneOptionStyleDropdownAsDiv,
  fieldBoxProps,
  gaAction,
  ...rest
}: IDropdownProps) => {
  const form = useForm();
  const { initialValues } = useFormState();
  const initialValue = (initialValues || {})[input.name];
  const isOnlyOneOption = options.length === 1;

  useEffect(() => {
    if (initialValue || isOnlyOneOption) {
      form.change(input.name, initialValue || options[0].value);
    }
  }, [input.name, initialValue, options]);

  const handleChange = (value: OptionValue): void => {
    onChangeFormElementComponent(
      { value },
      {
        input,
        setModifiedSinceLastBlur: form.mutators.setModifiedSinceLastBlur,
        onChange,
        emptyValueFunc: val => isEmpty(val)
      }
    );

    const gaActionToTrack = gaAction ?? `${GA_ACTIONS.SELECT} - dropdown ${input.name}`;
    trackDropdownSelect(JSON.stringify(value), gaActionToTrack);
  };

  const errorMessage = getErrorMessage(meta);

  return hideDropdown ? null : (
    <FormElementWrapper className={className} input={input} meta={meta}>
      <DropdownBase
        name={input.name}
        value={input.value}
        defaultValue={defaultValue}
        onChange={handleChange}
        options={options}
        {...rest}
        fieldBoxProps={{
          ...fieldBoxProps,
          error: parseErrorMessageToFieldBox(errorMessage),
          name: fieldBoxProps?.name || 'dropdown'
        }}
        oneOptionStyleDropdownAsDiv={oneOptionStyleDropdownAsDiv}
      />
    </FormElementWrapper>
  );
};

export default Dropdown;
